import { useState, type KeyboardEvent } from 'react';
import { Avatar, Button, StatusDot, Toggle } from '@/components';
import { AGENT_SCOPES, type Agent, type AgentScope, type ModelOption } from '@shared/types';
import './AgentBuilder.scss';

/** The draft an agent is edited through. `id: null` means "not saved yet". */
export interface AgentDraft {
  id: string | null;
  name: string;
  purpose: string;
  colour: string;
  rules: string[];
  useKnowledge: boolean;
  useApi: boolean;
  scopes: AgentScope[];
  model: string | null;
}

export interface AgentBuilderProps {
  draft: AgentDraft;
  models: ModelOption[];
  defaultModel: string;
  sourceCount: number;
  saving: boolean;
  onChange: (next: AgentDraft) => void;
  onSave: () => void;
  onDelete: () => void;
  onBack: () => void;
}

const SWATCHES = ['#2B93FF', '#1C3E61', '#2DD27A', '#F4A11D', '#FE5425', '#67789A'];

const SCOPE_LABELS: Record<AgentScope, string> = {
  stops: 'Stops',
  routes: 'Routes',
  drivers: 'Drivers',
  pod: 'Proof of delivery',
  invoices: 'Invoices',
};

export const emptyDraft = (): AgentDraft => ({
  id: null,
  name: '',
  purpose: '',
  colour: '#2B93FF',
  rules: [],
  useKnowledge: true,
  useApi: false,
  scopes: ['stops', 'routes'],
  model: null,
});

export const draftFromAgent = (agent: Agent): AgentDraft => ({
  id: agent.id,
  name: agent.name,
  purpose: agent.purpose,
  colour: agent.colour,
  rules: [...agent.rules],
  useKnowledge: agent.useKnowledge,
  useApi: agent.useApi,
  scopes: [...agent.scopes],
  model: agent.model,
});

export const AgentBuilder: React.FC<AgentBuilderProps> = ({
  draft,
  models,
  defaultModel,
  sourceCount,
  saving,
  onChange,
  onSave,
  onDelete,
  onBack,
}) => {
  const [ruleInput, setRuleInput] = useState('');

  const set = <K extends keyof AgentDraft>(key: K, value: AgentDraft[K]) =>
    onChange({ ...draft, [key]: value });

  function addRule() {
    const rule = ruleInput.trim();
    if (!rule) return;
    set('rules', [...draft.rules, rule]);
    setRuleInput('');
  }

  function onRuleKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      addRule();
    }
  }

  function toggleScope(scope: AgentScope) {
    set(
      'scopes',
      draft.scopes.includes(scope)
        ? draft.scopes.filter((s) => s !== scope)
        : [...draft.scopes, scope],
    );
  }

  const displayName = draft.name.trim() || 'New agent';
  const displayPurpose = draft.purpose.trim() || 'Give this agent a purpose so it knows its job.';
  const effectiveModel = draft.model ?? defaultModel;

  return (
    <div className="screen screen--wide builder">
      <button type="button" className="builder__back" onClick={onBack}>
        &#8592; Agents
      </button>
      <h1 className="builder__title">{draft.id ? 'Configure agent' : 'New agent'}</h1>

      <div className="builder__layout">
        <div className="builder__main">
          <section>
            <div className="field-label">Name</div>
            <input
              className="text-input"
              value={draft.name}
              placeholder="e.g. Dispatch AI"
              onChange={(e) => set('name', e.target.value)}
            />
          </section>

          <section>
            <div className="field-label">Avatar</div>
            <div className="builder__avatar-row">
              <Avatar name={displayName} colour={draft.colour} size={56} />
              <div className="builder__swatches">
                {SWATCHES.map((colour) => (
                  <button
                    key={colour}
                    type="button"
                    aria-label={`Use colour ${colour}`}
                    className={`builder__swatch${draft.colour === colour ? ' builder__swatch--active' : ''}`}
                    style={{ background: colour }}
                    onClick={() => set('colour', colour)}
                  />
                ))}
              </div>
            </div>
          </section>

          <section>
            <div className="field-label">Purpose</div>
            <textarea
              className="text-area"
              rows={3}
              value={draft.purpose}
              placeholder="What does this agent do, and for whom?"
              onChange={(e) => set('purpose', e.target.value)}
            />
          </section>

          <section>
            <div className="field-label">Model</div>
            <select
              className="select-input"
              value={draft.model ?? ''}
              onChange={(e) => set('model', e.target.value || null)}
            >
              <option value="">Workspace default ({defaultModel})</option>
              {models.map((m) => (
                <option key={m.model} value={m.model}>
                  {m.displayName} — ${m.inputPerMTok}/${m.outputPerMTok} per 1M
                </option>
              ))}
            </select>
          </section>

          <section>
            <div className="field-label">Rules</div>
            <div className="builder__rules">
              {draft.rules.map((rule, index) => (
                <div key={`${index}-${rule}`} className="builder__rule">
                  <span className="builder__rule-num">{index + 1}</span>
                  <span className="builder__rule-text">{rule}</span>
                  <button
                    type="button"
                    className="builder__rule-remove"
                    aria-label={`Remove rule ${index + 1}`}
                    onClick={() => set('rules', draft.rules.filter((_, i) => i !== index))}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <div className="builder__rule-add">
                <input
                  className="text-input"
                  value={ruleInput}
                  placeholder="Add a rule the agent must always follow…"
                  onChange={(e) => setRuleInput(e.target.value)}
                  onKeyDown={onRuleKey}
                />
                <Button variant="secondary" size="sm" onClick={addRule}>
                  Add
                </Button>
              </div>
            </div>
          </section>

          <section>
            <div className="field-label">Capabilities</div>
            <div className="builder__capabilities">
              <div className="builder__capability">
                <Toggle
                  checked={draft.useKnowledge}
                  onChange={(v) => set('useKnowledge', v)}
                  label="Knowledge library"
                  caption={
                    draft.useKnowledge
                      ? `${sourceCount} source${sourceCount === 1 ? '' : 's'} assigned to this agent.`
                      : 'This agent will not read any uploaded sources.'
                  }
                />
              </div>

              <div className="builder__capability">
                <Toggle
                  checked={draft.useApi}
                  onChange={(v) => set('useApi', v)}
                  label="Locate2u account access"
                  caption="Lets the agent query live stops, routes and drivers via the API."
                />
                {draft.useApi && (
                  <div className="builder__scopes">
                    {AGENT_SCOPES.map((scope) => (
                      <button
                        key={scope}
                        type="button"
                        className={`chip${draft.scopes.includes(scope) ? ' chip--active' : ''}`}
                        onClick={() => toggleScope(scope)}
                      >
                        {SCOPE_LABELS[scope]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        <aside className="builder__aside">
          <div className="builder__preview">
            <div className="builder__preview-label">Preview</div>
            <div className="builder__preview-identity">
              <Avatar name={displayName} colour={draft.colour} size={40} />
              <div className="builder__preview-name">{displayName}</div>
            </div>
            <p className="builder__preview-purpose">{displayPurpose}</p>
            <div className="builder__preview-facts">
              <div className="builder__preview-fact">
                <StatusDot tone="blue" size={6} />
                {draft.rules.length} rule{draft.rules.length === 1 ? '' : 's'}
              </div>
              <div className="builder__preview-fact">
                <StatusDot tone={draft.useKnowledge ? 'live' : 'idle'} size={6} />
                {draft.useKnowledge ? `${sourceCount} knowledge sources` : 'Knowledge off'}
              </div>
              <div className="builder__preview-fact">
                <StatusDot tone={draft.useApi ? 'live' : 'idle'} size={6} />
                {draft.useApi ? `${draft.scopes.length} API scopes` : 'No account access'}
              </div>
              <div className="builder__preview-fact">
                <StatusDot tone="blue" size={6} />
                {effectiveModel}
              </div>
            </div>
          </div>

          <Button fullWidth loading={saving} onClick={onSave}>
            {draft.id ? 'Save changes' : 'Create agent'}
          </Button>

          {draft.id && (
            <Button variant="danger" fullWidth onClick={onDelete}>
              Delete agent
            </Button>
          )}
        </aside>
      </div>
    </div>
  );
};

export default AgentBuilder;
